"use client";

import {
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { HINT, WIDGET_H3 } from "@/components/tool-kit/styles";
import { useToolLocale, useToolNumber } from "@/components/tools/tool-ui";
import type { UNIT_PLAN_GENERATOR_STRINGS } from "@/components/tools/unit-plan-generator";
import { tPlural } from "@/lib/i18n/interpolate";
import {
  MAX_MINUTES,
  MAX_PER_WEEK,
  MAX_WEEKS,
  MIN_MINUTES,
  MIN_PER_WEEK,
  MIN_WEEKS,
  MINUTES_STEP,
  type UnitInputs,
} from "@/lib/tool-kit/games-organizers/unit-plan";

type Strings = typeof UNIT_PLAN_GENERATOR_STRINGS;

const range = (low: number, high: number, step = 1) =>
  Array.from(
    { length: Math.floor((high - low) / step) + 1 },
    (_, i) => low + i * step
  );

const LIST_FIELDS = [
  ["goals", 3],
  ["understandings", 3],
  ["questions", 3],
  ["knowledge", 4],
  ["skills", 4],
] as const;

/** The unit's basics and the three stages, as fields. */
export default function UnitPlanForm({
  inputs,
  strings,
  onChange,
}: {
  inputs: UnitInputs;
  strings: Strings;
  onChange: (patch: Partial<UnitInputs>) => void;
}) {
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const numeric = (key: "weeks" | "perWeek" | "minutes") => (value: string) =>
    onChange({ [key]: Number(value) });
  return (
    <div className="space-y-8">
      <fieldset className="space-y-4">
        <legend className={WIDGET_H3}>{strings.basicsHeading}</legend>
        <TextField
          id="unit-title"
          label={strings.titleLabel}
          onChange={(title) => onChange({ title })}
          placeholder={strings.titlePlaceholder}
          value={inputs.title}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="unit-subject"
            label={strings.subjectLabel}
            onChange={(subject) => onChange({ subject })}
            placeholder={strings.subjectPlaceholder}
            value={inputs.subject}
          />
          <TextField
            id="unit-grade"
            label={strings.gradeLabel}
            onChange={(grade) => onChange({ grade })}
            placeholder={strings.gradePlaceholder}
            value={inputs.grade}
          />
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          <SelectField
            id="unit-weeks"
            label={strings.weeksLabel}
            onChange={numeric("weeks")}
            options={range(MIN_WEEKS, MAX_WEEKS).map((n) => ({
              value: String(n),
              label: tPlural(strings.weeksOption, locale, n, { count: fmt(n) }),
            }))}
            value={String(inputs.weeks)}
          />
          <SelectField
            id="unit-per-week"
            label={strings.perWeekLabel}
            onChange={numeric("perWeek")}
            options={range(MIN_PER_WEEK, MAX_PER_WEEK).map((n) => ({
              value: String(n),
              label: fmt(n),
            }))}
            value={String(inputs.perWeek)}
          />
          <SelectField
            id="unit-minutes"
            label={strings.minutesLabel}
            onChange={numeric("minutes")}
            options={range(MIN_MINUTES, MAX_MINUTES, MINUTES_STEP).map((n) => ({
              value: String(n),
              label: tPlural(strings.minutesOption, locale, n, {
                count: fmt(n),
              }),
            }))}
            value={String(inputs.minutes)}
          />
        </div>
      </fieldset>

      <fieldset className="space-y-4">
        <legend className={WIDGET_H3}>{strings.stage1}</legend>
        <p className={HINT}>{strings.stage1Hint}</p>
        <div className="grid gap-4 sm:grid-cols-2">
          {LIST_FIELDS.map(([key, rows]) => (
            <TextAreaField
              hint={strings.listHint}
              id={`unit-${key}`}
              key={key}
              label={strings.fields[key]}
              onChange={(value) => onChange({ [key]: value })}
              prose
              rows={rows}
              value={inputs[key]}
            />
          ))}
        </div>
      </fieldset>

      <fieldset className="space-y-4">
        <legend className={WIDGET_H3}>{strings.stage2}</legend>
        <p className={HINT}>{strings.stage2Hint}</p>
        <TextField
          id="unit-task-title"
          label={strings.fields.taskTitle}
          onChange={(taskTitle) => onChange({ taskTitle })}
          value={inputs.taskTitle}
        />
        <TextAreaField
          id="unit-task-description"
          label={strings.fields.taskDescription}
          onChange={(taskDescription) => onChange({ taskDescription })}
          prose
          rows={3}
          value={inputs.taskDescription}
        />
        <TextAreaField
          hint={strings.listHint}
          id="unit-evidence"
          label={strings.fields.evidence}
          onChange={(evidence) => onChange({ evidence })}
          prose
          rows={3}
          value={inputs.evidence}
        />
      </fieldset>

      <fieldset className="space-y-4">
        <legend className={WIDGET_H3}>{strings.stage3}</legend>
        <p className={HINT}>{strings.stage3Hint}</p>
        <TextAreaField
          hint={strings.lessonsHint}
          id="unit-lessons"
          label={strings.fields.lessons}
          onChange={(lessons) => onChange({ lessons })}
          prose
          rows={8}
          value={inputs.lessons}
        />
      </fieldset>
    </div>
  );
}
